"use client";

import { type RefObject, useCallback, useEffect, useState } from "react";

/**
 * Full-screen play for a classroom display. Asks the browser for real full screen and, where it
 * refuses (some phones, embedded views), falls back to an overlay that fills the window. Leaving
 * either way — the browser's own exit, or Escape for the overlay — clears the flag.
 */
export function useFullScreen(frame: RefObject<HTMLElement | null>) {
  const [wide, setWide] = useState(false);

  useEffect(() => {
    function onChange() {
      if (!document.fullscreenElement) {
        setWide(false);
      }
    }
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const set = useCallback(
    async (next: boolean) => {
      setWide(next);
      try {
        if (next) {
          await frame.current?.requestFullscreen?.();
        } else if (document.fullscreenElement) {
          await document.exitFullscreen();
        }
      } catch {
        // The overlay still fills the window where the browser refuses real full screen.
      }
    },
    [frame]
  );

  return { wide, set };
}

/** Class names for the frame: a fixed overlay while `wide`. */
export const FULL_SCREEN_FRAME =
  "fixed inset-0 z-50 overflow-auto bg-background p-4 sm:p-8";
